/**
 * Campaign report SCHEMA — the shared data contract.
 *
 * Owned here (not in the template folder) because both the integrations
 * adapter (campaignToReport) and the campaign-overview template depend on it.
 * One-directional: templates → schema; schema imports nothing above it.
 */
/** Pure Campaign Overview report template — Brainiacs brand system. No Leads Intelligence, network, storage, or browser dependencies. */
export const CAMPAIGN_TEMPLATE = 'campaign-overview';
export const CAMPAIGN_REVISION = '1.1.3';
export type CampaignStage = { key: string; label: string; value: number; color: string; highlight?: boolean };
/** One-line opportunity digest under a directory row — the row's strongest
 *  detected opportunity. Free prose + display vocabulary (type, confidence). */
export type CampaignRowOpportunity = { summary: string; type?: string; confidence?: string };
export type CampaignLeadRow = { name: string; role: string; company: string; statusKey: string; statusLabel?: string; /** Company logo URL (companies directory variant). */ companyLogoUrl?: string; /** "City, Country" for the companies directory. */ location?: string; /** Employee range ("51-200") for the companies directory. */ employeeRange?: string; /** Top detected opportunity — renders as a summary line under the row. */ opportunity?: CampaignRowOpportunity };
export interface CampaignReport {
  schemaVersion: 1;
  locale: string;
  timezone: string;
  capturedAt: string;
  reference?: string;
  brand: { name: string; color: string; logoUrl?: string };
  campaign: { name: string; status?: string; objective?: string };
  stages: CampaignStage[];
  /** Directory variant the renderer draws: 'leads' = name/role/company/status
   *  (default when undefined); 'companies' = logo/company/location/employees. */
  directory?: 'leads' | 'companies';
  leads: CampaignLeadRow[];
}
export const COPY = {
  reportTitle: 'Campaign overview report', pipeline: 'Pipeline status', directory: 'Lead directory', campaign: 'Campaign',
  locationCol: 'Location', employeesCol: 'Employees',
  captured: 'Data as of', stage: 'Stage', noLeads: 'No leads have been added to this campaign yet.',
  confidential: 'Confidential intelligence', poweredBy: 'Powered by Brainiacs AI Intelligence', compiledFor: 'Compiled for', reference: 'Doc ID',
  leadCol: 'Lead', roleCol: 'Role', companyCol: 'Company', statusCol: 'Status',
};
/** Reader-facing definitions travel through the report translation contract. */
export const PIPELINE_STAGE_DESCRIPTIONS: Record<string, string> = {
  new_lead: 'New lead generated and ready to be processed.',
  enriching: 'Company and contact data are being enriched.',
  enriched: 'Company and contact data have been enriched and are ready for outreach.',
  outreaching: 'The SDR agent is contacting the company by phone or email.',
  outreached: 'The SDR agent has established contact with the prospect.',
  engaged: 'The SDR agent is qualifying and validating the prospect.',
  proposal: 'A proposal has been sent by the partner or manufacturer.',
  converted: 'The proposal has been won by the partner or manufacturer.',
  discarded: 'Discarded due to low ICP fit, an already covered need, or outsourcing.',
};
export type Translations = Record<string, string>;
export const esc = (v: unknown) => String(v ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
// The objective is free text from the campaign form: escape it ONCE here, then
// swap newlines for <br> — the caller must not escape again.
export const lineBreaks = (escaped: string) => escaped.replace(/\n/g, '<br>');
const record = (v: unknown): v is Record<string, any> => !!v && typeof v === 'object' && !Array.isArray(v);
const text = (v: unknown, max = 18000) => typeof v === 'string' && v.length <= max;
const validDate = (v: unknown) => typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(v) && Number.isFinite(Date.parse(v));
const validAsset = (v: unknown) => typeof v === 'string' && (/^https:\/\/[^\s"'<>]{1,2000}$/.test(v) || (/^data:image\/(png|jpeg);base64,[A-Za-z0-9+/=]+$/.test(v) && v.length <= 180000));
const HEX = /^#[\da-f]{6}$/i, KEY = /^[a-z0-9_-]{1,36}$/;
export function validateCampaignReport(value: unknown): asserts value is CampaignReport {
  const d = value as CampaignReport;
  if (!record(d) || d.schemaVersion !== 1 || !text(d.locale, 35) || !text(d.timezone, 80) || !validDate(d.capturedAt)) throw new Error('Invalid report identity, language, timezone, or capture time.');
  try { new Intl.DateTimeFormat(d.locale, { timeZone: d.timezone }); } catch { throw new Error('Invalid report language or timezone.'); }
  if (d.reference !== undefined && !text(d.reference, 120)) throw new Error('Invalid report reference.');
  if (!record(d.brand) || !text(d.brand.name, 160) || !HEX.test(d.brand.color)) throw new Error('Brand requires a name and a six-digit hex color.');
  if (d.brand.logoUrl !== undefined && !validAsset(d.brand.logoUrl)) throw new Error('Logo must be an HTTPS URL or an embedded PNG/JPEG under 130 KB.');
  if (!record(d.campaign) || !text(d.campaign.name, 300) || !d.campaign.name.trim()) throw new Error('Campaign name is required.');
  if (d.campaign.status !== undefined && !text(d.campaign.status, 120)) throw new Error('Invalid campaign status.');
  if (d.campaign.objective !== undefined && !text(d.campaign.objective, 4000)) throw new Error('Invalid campaign objective.');
  if (!Array.isArray(d.stages) || d.stages.length > 12) throw new Error('Up to 12 pipeline stages are allowed.');
  const keys = new Set<string>();
  let highlights = 0;
  for (const s of d.stages) {
    if (!record(s) || !text(s.key, 36) || !KEY.test(s.key) || keys.has(s.key) || !text(s.label, 120) || !Number.isFinite(s.value) || s.value < 0 || s.value > 1e9 || !HEX.test(s.color)) throw new Error('Invalid, duplicated, or uncolored pipeline stage.');
    if (s.highlight) highlights++;
    keys.add(s.key);
  }
  if (highlights > 4) throw new Error('At most 4 stages can be highlighted in the masthead.');
  // No practical cap — large campaigns (10K+ leads) paginate across pages.
  if (!Array.isArray(d.leads)) throw new Error('Lead rows must be an array.');
  if (d.directory !== undefined && !['leads','companies'].includes(d.directory)) throw new Error('Invalid directory variant.');
  for (const l of d.leads) {
    if (!record(l) || !text(l.name, 400) || !l.name.trim() || !text(l.role, 400) || !text(l.company, 400) || !text(l.statusKey, 36) || !KEY.test(l.statusKey)) throw new Error('Invalid lead row.');
    const known = keys.has(l.statusKey);
    if (l.statusLabel !== undefined && !text(l.statusLabel, 120)) throw new Error('Invalid lead status label.');
    if (!known && l.statusLabel === undefined) throw new Error('Lead rows need a statusLabel when their status is not a pipeline stage.');
    // Companies-directory fields (optional, validated when present).
    if (l.companyLogoUrl !== undefined && !/^https:\/\/[^\s"'<>]{1,2000}$/.test(l.companyLogoUrl)) throw new Error('Company logo must be an HTTPS URL.');
    if (l.location !== undefined && !text(l.location, 200)) throw new Error('Invalid company location.');
    if (l.employeeRange !== undefined && !text(l.employeeRange, 60)) throw new Error('Invalid employee range.');
    // Opportunity digest (optional): prose + display vocabulary only — it is
    // a one-line summary under the row, not a full card.
    if (l.opportunity !== undefined) {
      const o = l.opportunity;
      if (!record(o) || !text(o.summary, 500) || !o.summary.trim()) throw new Error('Lead row opportunity needs summary prose.');
      if (o.type !== undefined && !text(o.type, 120)) throw new Error('Invalid opportunity type.');
      if (o.confidence !== undefined && !text(o.confidence, 12)) throw new Error('Invalid opportunity confidence.');
    }
  }
  // Opportunities sheets (optional — legacy payloads carry neither). The 50
  // wall matches the top-25-per-type aggregation cap with headroom.
  for (const key of ['campaignOpportunities','generalOpportunities'] as const) {
    const list = d[key];
    if (list === undefined) continue;
    if (!Array.isArray(list) || list.length > 50) throw new Error(`Invalid ${key} list.`);
    for (const o of list) {
      if (!record(o) || !text(o.need, 2000) || !o.need.trim()) throw new Error(`Opportunity cards in ${key} need prose (need).`);
      for (const t of ['type','category','urgency','confidence','amount','timeline','country','decisionMaker','leadName','companyName'] as const) {
        if (o[t] !== undefined && !text(o[t], 400)) throw new Error(`Invalid opportunity ${t} in ${key}.`);
      }
      if (o.evidence !== undefined && (!Array.isArray(o.evidence) || o.evidence.length > 20 || !o.evidence.every(e => text(e, 500)))) throw new Error(`Invalid opportunity evidence in ${key}.`);
    }
  }
  // Large campaigns: 10K+ lead rows ≈ several MB of JSON. The size walls are
  // the executions-API body limit and Gotenberg's patience, not this number.
  if (JSON.stringify(d).length > 8000000) throw new Error('Report payload is too large (8MB).');
}
/** Translatable: labels, stage names, objective. Lead names/roles/companies and keys are not. */
export function translationTexts(d: CampaignReport): Translations {
  validateCampaignReport(d);
  const out: Translations = Object.fromEntries(Object.entries(COPY).map(([k,v]) => [`label.${k}`, v]));
  out['legend.title'] = 'Legend · What each stage means';
  d.stages.forEach(s => {
    out[`stage.${s.key}`] = s.label;
    if (PIPELINE_STAGE_DESCRIPTIONS[s.key]) out[`stageDescription.${s.key}`] = PIPELINE_STAGE_DESCRIPTIONS[s.key];
  });
  // Campaign status / objective are closed vocabularies — map the stored value
  // to its vocabulary key so the dictionary (or the AI translator) resolves it.
  // Keys are PREFIXED to match the renderer's lookups (`campaignStatus.<key>`,
  // `objective.<key>`) — an unprefixed key here is silently ignored at render
  // time, which is what left "ACTIVE"/"Lead Generation" in English.
  // The prefix also keeps `objective.<key>` from colliding with the flat `label.*`
  // namespace if a `label.objective` is ever added.
  if (d.campaign.status) out[`campaignStatus.${statusKeyOf(d.campaign.status)}`] = d.campaign.status;
  if (d.campaign.objective) out[`objective.${objectiveKeyOf(d.campaign.objective)}`] = d.campaign.objective;
  // Opportunity digest prose translates like any other free text on the report
  // (display vocabulary — type, confidence — does not).
  d.leads.forEach((l,i) => { if (l.opportunity) out[`lead.${i}.opportunity`] = l.opportunity.summary; });
  return out;
}
export function validateTranslations(d: CampaignReport, translated: unknown): asserts translated is Translations {
  const source = translationTexts(d);
  if (!record(translated) || Object.keys(translated).length !== Object.keys(source).length || !Object.keys(source).every(k => Object.hasOwn(translated, k) && text(translated[k], 50000) && (!source[k].trim() || translated[k].trim()))) throw new Error('Translation is incomplete. Every text key must be returned.');
}
const BRAINIACS_LOGO = 'https://framerusercontent.com/images/cqIm0c8fa5DLXhS0U0teULXMNMk.png?width=512&height=512';
/** Closed vocabularies — stored values map to a stable key the dictionary
 *  and translator both use. Unknown values pass through unchanged. */
export const vocabKey = (value: string, keys: readonly string[]): string => {
  const v = String(value || '').trim().toLowerCase().replace(/[\s-]+/g, '_');
  return keys.includes(v) ? v : value;
};
export const CAMPAIGN_STATUS_KEYS = ['active','paused','archived'] as const;
export const OBJECTIVE_KEYS = ['lead_generation','appointment_booking','product_demo_requests','webinar_registration','free_trial_signups','partnership_opportunities'] as const;
export const statusKeyOf = (v: string) => vocabKey(v, CAMPAIGN_STATUS_KEYS);
export const objectiveKeyOf = (v: string) => vocabKey(v, OBJECTIVE_KEYS);
